import type {
  VariableDefinition,
  VariableDefinitions,
  VariableValueErrors,
} from './variable_definitions.js';
import { validateVariableValues, type VariableValuesResult } from './variable_definitions.js';

// ─────────────────────────────────────────────────────────────────
// Merge-field value editing
//
// Definitions describe a merge field; these are the pure helpers that turn
// values into the strings a form shows and back into the typed values
// `validateVariableValues` accepts. They live here, not in a component,
// because three surfaces capture values over the same definitions — the
// send-test dialog, the subscriber editor, and CSV import — and a second
// implementation would be a second set of coercion rules to keep in step.
// ─────────────────────────────────────────────────────────────────

/** Render one typed value as the string an input field shows. */
export function variableValueToInput(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) {
    // Two kinds of array reach here. An `array` holds words, which read best
    // comma separated; a `list` holds items — records — and JSON is the only
    // text that says what one is, so those round-trip as JSON.
    return value.some((item) => item !== null && typeof item === 'object')
      ? JSON.stringify(value)
      : value.map((item) => String(item)).join(', ');
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * Seed inputs for every definition: the stored value when there is one,
 * otherwise the definition's default, so a field that ships a default (or a
 * contact who already has a value) arrives pre-filled.
 */
export function initialVariableInputs(
  definitions: VariableDefinitions,
  values: Record<string, unknown> = {},
): Record<string, string> {
  const inputs: Record<string, string> = {};
  for (const [name, definition] of Object.entries(definitions)) {
    const stored = values[name];
    const chosen = stored !== undefined ? stored : definition.default;
    if (chosen === undefined) continue;
    inputs[name] = variableValueToInput(chosen);
  }
  return inputs;
}

/**
 * Turn the raw input strings into typed values for `validateVariableValues`:
 * the form holds text, the definitions demand types.
 */
export function collectVariableValues(
  definitions: VariableDefinitions,
  inputs: Record<string, string>,
): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const [name, definition] of Object.entries(definitions)) {
    const input = (inputs[name] ?? '').trim();
    // Blank means "not supplied": optional variables are simply omitted, a
    // required one is then reported by the schema.
    if (input === '') continue;

    switch (definition.type) {
      case 'number': {
        const value = Number(input);
        // Non-numeric text is passed through untouched so validation reports it.
        values[name] = Number.isFinite(value) ? value : input;
        break;
      }
      case 'boolean':
        values[name] = input === 'true';
        break;
      case 'array':
        values[name] = input
          .split(',')
          .map((item) => item.trim())
          .filter((item) => item !== '');
        break;
      case 'list': {
        // A list's value is its items, so the field holds JSON. Text that is not
        // a JSON array is passed through untouched, exactly as non-numeric text
        // is for a number, so validation names the real problem.
        values[name] = parseJsonArray(input) ?? input;
        break;
      }
      default:
        values[name] = input;
    }
  }
  return values;
}

/** A string that is a JSON array of items, or `null` when it is not. */
function parseJsonArray(text: string): unknown[] | null {
  if (!text.startsWith('[')) return null;
  try {
    const parsed: unknown = JSON.parse(text);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** One message per input, keyed by variable name (the first issue wins). */
export function variableInputErrors(errors: VariableValueErrors): Record<string, string> {
  const byName: Record<string, string> = {};
  for (const issue of errors) {
    const name = typeof issue.path[0] === 'string' ? issue.path[0] : '';
    if (name !== '' && byName[name] === undefined) byName[name] = issue.message;
  }
  return byName;
}

/** Example text for one field's input, so the expected shape is visible. */
export function variablePlaceholder(definition: VariableDefinition): string {
  if (definition.type === 'url') return 'https://example.com';
  if (definition.type === 'data_image_url') return 'https://… or data:image/…';
  if (definition.type === 'number') return '42';
  if (definition.type === 'array') return 'one, two, three';
  // A list's sample item is the best example there is, because it is the shape
  // the item template reads; without one, the field names say it instead.
  if (definition.type === 'list') {
    const [sample] = definition.default ?? [];
    if (sample !== undefined) return JSON.stringify([sample]);
    const fields = Object.keys(definition.vars ?? {});
    return fields.length > 0
      ? JSON.stringify([Object.fromEntries(fields.map((field) => [field, '']))])
      : '[]';
  }
  return definition.default !== undefined ? variableValueToInput(definition.default) : '';
}

/**
 * Validate a values record that is already typed (from the API or from
 * {@link collectVariableValues}) against the definitions that own it.
 * A thin re-export so call sites read as "validate this record", and every
 * write path reports failures the same way.
 */
export function validateAttributeValues(
  definitions: VariableDefinitions,
  values: Record<string, unknown>,
): VariableValuesResult {
  return validateVariableValues(definitions, values);
}

/**
 * The merge values a **render** uses: the definitions' own defaults, with
 * whatever the caller supplied layered over them, checked as a whole.
 *
 * This is what makes "show me this email" work without every caller having to
 * know which variables have defaults: an email whose `plan` defaults to `free`
 * renders with that default, and a caller that passes `{ plan: 'pro' }` gets
 * `pro`.
 *
 * Types are enforced, **presence is not** — a required variable that nothing
 * supplies is rendered as an empty merge field, which is exactly what
 * `validateVariableValues` already does and what the editor's Preview shows.
 * A render is a faithful picture of the document, not a validation gate; the
 * *send* path is where "required" is enforced (see prepareVariables in
 * apps/server/src/email/sending.ts).
 *
 * Both a preview (nobody supplied anything, defaults stand in) and a targeted
 * render (a caller supplying values for one recipient) go through here, so the
 * two cannot disagree about what an email renders as.
 */
export function resolveVariableValues(
  definitions: VariableDefinitions,
  supplied?: Record<string, unknown> | undefined,
): VariableValuesResult {
  // `!== undefined`, not truthiness: `false`, `0` and `''` are values a
  // definition may legitimately default to, and dropping them would render an
  // empty merge field for a variable that has a perfectly good default.
  const defaults: Record<string, unknown> = {};
  for (const [name, definition] of Object.entries(definitions)) {
    if (definition.default !== undefined) defaults[name] = definition.default;
  }

  return validateVariableValues(definitions, { ...defaults, ...(supplied ?? {}) });
}

/**
 * The variables marked required that a values record leaves unfilled, in
 * definition order.
 *
 * `validateVariableValues` deliberately tolerates a missing required value —
 * which is right for a *render* (a picture of the document) and for an email
 * being edited. A **delivery** is the case where it is not: there is no
 * audience to fill the gap per recipient and no second chance, so the send
 * paths ask this question first and refuse with the names.
 *
 * An empty string counts as supplied: someone who deliberately sets a variable
 * to `''` has made a choice, and a definition whose default is `''` is a
 * deliberate default.
 */
export function unfilledRequiredVariables(
  definitions: VariableDefinitions,
  values: Record<string, unknown>,
): string[] {
  return Object.entries(definitions)
    .filter(([, definition]) => definition.required)
    .map(([name]) => name)
    .filter((name) => values[name] === undefined);
}
